import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { MICROSITES } from './micrositeData'
import './microsite.css'

const AGENT_KEYS = ['wind', 'sierra', 'media', 'chess']

const AgentMicrosite = ({ agentKey: explicitAgentKey }) => {
  const params = useParams()
  const [copiedPrompt, setCopiedPrompt] = useState(null)

  // Determine agent configuration from prop or route slug
  const rawKey = (explicitAgentKey || params.agent || params.slug || 'wind').toLowerCase().trim()
  let matchedKey = 'wind'

  if (MICROSITES[rawKey]) {
    matchedKey = rawKey
  } else {
    // Check alternate slugs (e.g. tichess, movies, cinema, windsavant, sierraguide)
    for (const [key, data] of Object.entries(MICROSITES)) {
      if (data.slug === rawKey || (data.alternateSlugs && data.alternateSlugs.includes(rawKey))) {
        matchedKey = key
        break
      }
    }
  }

  const agent = MICROSITES[matchedKey]

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    const pageTitle = `${agent.name} · ${agent.tagline} | Dionyziuz`
    document.title = pageTitle

    const updateMeta = (attr, key, val) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', val)
    }

    const absImage = `https://dionyziuz.com/agents/${agent.id}.jpg`
    const absUrl = `https://dionyziuz.com/${agent.slug}`

    updateMeta('name', 'description', agent.leadParagraph)
    updateMeta('property', 'og:title', pageTitle)
    updateMeta('property', 'og:description', agent.leadParagraph)
    updateMeta('property', 'og:image', absImage)
    updateMeta('property', 'og:image:secure_url', absImage)
    updateMeta('property', 'og:url', absUrl)
    updateMeta('property', 'og:type', 'website')
    updateMeta('property', 'og:site_name', 'Dionyziuz')
    updateMeta('name', 'twitter:card', 'summary_large_image')
    updateMeta('name', 'twitter:title', pageTitle)
    updateMeta('name', 'twitter:description', agent.leadParagraph)
    updateMeta('name', 'twitter:image', absImage)
  }, [matchedKey, agent])

  const handleCopyPrompt = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedPrompt(text)
        setTimeout(() => setCopiedPrompt(null), 2500)
      })
    }
  }

  return (
    <div className={`micrositePage microsite--${agent.id}`}>
      {/* Top Navigation & Agent Switcher */}
      <div className="micrositeNav">
        <div className="micrositeNavContainer">
          <Link to="/" className="backHomeLink">
            <span aria-hidden="true">&larr;</span> Back to Dionyziuz Suite
          </Link>
          <div className="agentSwitcher">
            {AGENT_KEYS.map((k) => {
              const a = MICROSITES[k]
              const isActive = k === matchedKey
              return (
                <Link
                  key={k}
                  to={`/${a.slug}`}
                  className={`switcherPill ${isActive ? 'switcherPill--active' : ''}`}
                >
                  <img src={a.avatar} alt={a.name} className="switcherAvatar" />
                  <span>{a.name}</span>
                </Link>
              )
            })}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="micrositeHero">
        <div className="micrositeContainer">
          <div className="heroTopBadge">
            <span className="badgeDot" />
            <span>{agent.badge}</span>
          </div>

          <div className="heroMain">
            <div className="heroAvatarCol">
              <div className="heroAvatarGlow" style={{ borderColor: agent.themeColor }}>
                <img src={agent.avatar} alt={`${agent.name} avatar`} className="heroAvatarImg" />
              </div>
            </div>

            <div className="heroTextCol">
              <h1 className="heroTitle">{agent.name}</h1>
              <p className="heroTagline">{agent.tagline}</p>
              <p className="heroLead">{agent.leadParagraph}</p>

              <div className="heroCtas">
                <a
                  href={agent.telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primaryTelegramBtn"
                  style={{ background: agent.themeColor }}
                >
                  <span>Chat with {agent.name} on Telegram</span>
                  <span className="extArrow" aria-hidden="true">&#8599;</span>
                </a>
                <a
                  href="#demo"
                  className="secondaryGhostBtn"
                >
                  See Interactive Demo &darr;
                </a>
              </div>

              <div className="heroMetaPills">
                <span className="metaPill">✨ <strong>10 Try-Out Questions</strong> (Free ongoing access)</span>
                <span className="metaPill">🔒 <strong>Encrypted &amp; Private</strong></span>
                <span className="metaPill">⚡ <strong>Telegram App &amp; Web</strong></span>
                <span className="metaPill">🧠 <strong>Self-Learning Memory</strong></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Narrative */}
      <section className="micrositeOverviewSection">
        <div className="micrositeContainer">
          <div className="overviewCard">
            <h2 className="sectionSubheading">Why We Built {agent.name}</h2>
            <p className="overviewText">{agent.overview}</p>
            <div className="previewNoticeStrip">
              <span className="previewNoticeIcon">🎁</span>
              <span><strong>Instant Access:</strong> {agent.previewQuotaNotice}</span>
            </div>
            {agent.satelliteNotice && (
              <div
                className="previewNoticeStrip"
                style={{
                  marginTop: '0.85rem',
                  borderColor: 'rgba(5, 150, 105, 0.4)',
                  background: 'rgba(5, 150, 105, 0.08)'
                }}
              >
                <span className="previewNoticeIcon">🛰️</span>
                <span>
                  <strong>Off-Grid Satellite Access:</strong> {agent.satelliteNotice}{' '}
                  <Link
                    to="/contact-us"
                    style={{
                      color: '#059669',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      marginLeft: '6px'
                    }}
                  >
                    Contact us &rarr;
                  </Link>
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Interactive Telegram Chat Mockup Demo */}
      <section className="micrositeDemoSection" id="demo">
        <div className="micrositeContainer">
          <div className="sectionHeader">
            <span className="sectionCategory">Interactive Preview</span>
            <h2>Experience What It Delivers</h2>
            <p className="sectionDesc">
              A real glimpse into how {agent.name} reasons, formats live telemetry, and renders visual intelligence in Telegram.
            </p>
          </div>

          <div className="telegramDeviceFrame">
            <div className="deviceHeader">
              <div className="deviceHeaderLeft">
                <img src={agent.avatar} alt={agent.name} className="deviceAvatar" />
                <div className="deviceBotInfo">
                  <span className="deviceBotName">{agent.name}</span>
                  <span className="deviceBotStatus">bot · online</span>
                </div>
              </div>
              <a
                href={agent.telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="deviceLaunchLink"
              >
                Open in Telegram &rarr;
              </a>
            </div>

            <div className="deviceChatBody">
              <div className="chatDateDivider">Today · Try-Out Session</div>

              {/* User Query Message */}
              <div className="chatBubble chatBubble--user">
                <div className="bubbleText">{agent.demo.userQuery}</div>
                <div className="bubbleTime">13:42</div>
              </div>

              {/* Agent Response Message */}
              <div className="chatBubble chatBubble--agent">
                <div className="bubbleAgentHeader">
                  <strong>{agent.demo.agentIntro}</strong>
                </div>
                <div className="bubbleText">
                  {agent.demo.agentContent.map((line, idx) => (
                    <div key={idx} className="demoLine">
                      {line || <br />}
                    </div>
                  ))}
                </div>
                <div className="demoVisualAttachment">
                  <span className="attachmentIcon">📊</span>
                  <span>{agent.demo.demoNote}</span>
                </div>
                <div className="bubbleTime">13:42 · Delivered via CoALA Engine</div>
              </div>
            </div>

            <div className="deviceFooterAction">
              <a
                href={agent.telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="deviceCtaBtn"
                style={{ background: agent.themeColor }}
              >
                Ask This in Telegram (10 Try-Out Questions) &#8599;
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Deep-Dive Capabilities / Pillars */}
      <section className="micrositePillarsSection">
        <div className="micrositeContainer">
          <div className="sectionHeader">
            <span className="sectionCategory">Core Capabilities</span>
            <h2>Engineered for Deep Domain Authority</h2>
            <p className="sectionDesc">
              Not a generic chatbot wrapping broad search results. {agent.name} is armed with specialized sandboxed skills, real-time live APIs, and continuous memory consolidation.
            </p>
          </div>

          <div className="pillarsGrid">
            {agent.pillars.map((pillar, idx) => (
              <div key={idx} className="pillarCard">
                <div className="pillarIndex">0{idx + 1}</div>
                <h3 className="pillarTitle">{pillar.title}</h3>
                <span className="pillarTagline">{pillar.tagline}</span>
                <p className="pillarDesc">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Sample Prompts Grid */}
      <section className="micrositePromptsSection">
        <div className="micrositeContainer">
          <div className="sectionHeader">
            <span className="sectionCategory">Try It Yourself</span>
            <h2>Curated Prompts to Try Out {agent.name}</h2>
            <p className="sectionDesc">
              Tap any prompt below to copy it to your clipboard, then paste it directly to {agent.name} in Telegram.
            </p>
          </div>

          <div className="promptsGrid">
            {agent.samplePrompts.map((prompt, idx) => (
              <div
                key={idx}
                className="promptCard"
                onClick={() => handleCopyPrompt(prompt)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleCopyPrompt(prompt)}
              >
                <p className="promptText">&ldquo;{prompt}&rdquo;</p>
                <div className="promptCardFooter">
                  <span className="copyActionText">
                    {copiedPrompt === prompt ? '✓ Copied to clipboard!' : '📋 Tap to copy'}
                  </span>
                  <a
                    href={`${agent.telegramUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tryPromptLink"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Send to Telegram &rarr;
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Slash Command Reference */}
      <section className="micrositeCommandsSection">
        <div className="micrositeContainer">
          <div className="sectionHeader">
            <span className="sectionCategory">Telegram Command Menu</span>
            <h2>Instant Slash Commands</h2>
            <p className="sectionDesc">
              Type or tap any of these native Telegram commands anytime to launch specific workflows.
            </p>
          </div>

          <div className="commandsTableCard">
            <div className="commandsGrid">
              {agent.commands.map((cmdItem, idx) => (
                <div key={idx} className="commandRow">
                  <code className="commandCode">{cmdItem.cmd}</code>
                  <span className="commandDesc">{cmdItem.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How to Get Started in 3 Steps */}
      <section className="micrositeHowToSection">
        <div className="micrositeContainer">
          <div className="howToCard">
            <h2 className="howToTitle">How to Get Started in 30 Seconds</h2>
            <div className="howToSteps">
              <div className="howToStep">
                <div className="stepBadge">Step 1</div>
                <h3>Open Telegram</h3>
                <p>
                  Click the button below to launch {agent.name}. If you don&apos;t have Telegram installed yet, you can <a href="https://telegram.org" target="_blank" rel="noopener noreferrer">download it free</a> for any phone or desktop, or use <strong>Telegram Web</strong> in your browser with zero installations.
                </p>
              </div>
              <div className="howToStep">
                <div className="stepBadge">Step 2</div>
                <h3>Tap Start</h3>
                <p>
                  Hit <strong>Start</strong> in Telegram to immediately activate your <strong>10 try-out questions</strong>. No sign-up, email verification, or credit card required. Continued access after approval is completely free.
                </p>
              </div>
              <div className="howToStep">
                <div className="stepBadge">Step 3</div>
                <h3>Experience &amp; Train It</h3>
                <p>
                  Ask complex queries or teach the agent your preferences. If an answer ever misses what you wanted, reply: <em>&ldquo;I expected you to...&rdquo;</em> and it permanently remembers for next time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust, Safety & Telegram TOC Banner */}
      <section className="micrositeTrustSection">
        <div className="micrositeContainer">
          <div className="trustBanner">
            <div className="trustBannerContent">
              <h3>Built on Telegram: Fast, Secure &amp; Privacy-Focused</h3>
              <p>
                We chose Telegram because it offers state-of-the-art cloud encryption, lightning-fast message delivery, zero intrusive ad-profiling, and universal cross-device sync. Your conversations are governed by Telegram&apos;s strict security framework.
              </p>
              <div className="trustLinksGroup">
                <a href="https://telegram.org/tos" target="_blank" rel="noopener noreferrer">
                  Telegram Terms of Service (TOC) &rarr;
                </a>
                <a href="https://telegram.org/privacy" target="_blank" rel="noopener noreferrer">
                  Telegram Privacy Policy &rarr;
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Call to Action */}
      <section className="micrositeFinalCta">
        <div className="micrositeContainer">
          <div className="finalCtaCard" style={{ background: agent.themeGradient }}>
            <img src={agent.avatar} alt={agent.name} className="finalCtaAvatar" />
            <h2>Ready to put {agent.name} to the test?</h2>
            <p>
              Start chatting immediately with 10 try-out questions. Put real-world conditions, routes, and questions to the test right now — continued access is completely free upon approval.
            </p>
            <a
              href={agent.telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="finalCtaBtn"
            >
              Launch {agent.name} on Telegram &rarr;
            </a>
            <span className="finalCtaSub">
              {agent.handle} · Instant Telegram Web or App Launch
            </span>
          </div>
        </div>
      </section>
    </div>
  )
}

export default AgentMicrosite
